import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs';

import {
  QueueEmailPendingPayload,
  QueueFileNotificationPayload,
  QueueFilePendingPayload,
  QueueJobStatus,
  QueueJobType,
  QueueNotificationModel,
  QueueNotificationPayload,
  QueuePendingOperation,
  QueuePendingPayload,
} from '@models/api/queue.model';
import { AttachmentModel } from '@models/module/fields/main';
import { HubHandlerSourceAngular } from '@models/system/signalR';
import { GetFolderParams, MyDriveService } from '@module-instance/my-drive/common';
import { printBlob } from '@shared/utils';
import { FileService } from './file.service';
import { HttpService } from './http.service';
import { NotificationService } from './notification.service';
import { SignalRService } from './signalR.service';
import { UserService } from './user.service';

export const QUEUE_NOTIFICATION_HUB_METHOD = 'QueueNotification';
export const QUEUE_STATUS_POLL_INTERVAL_MS = 30_000; // 30 seconds
export const QUEUE_OPERATION_MAX_WAIT_MS = 600_000; // 10 minutes (general default)
export const QUEUE_PRINT_OPERATION_MAX_WAIT_MS = 3_600_000; // 60 minutes (matches PsPdfKitOptions.ServerApiTimeoutMinutes)
const SETTLED_OPERATION_TTL_MS = 60_000; // 1 minute

interface TrackedQueueOperation {
  operation: QueuePendingOperation;
  startedAt: number;
  pollIntervalId?: ReturnType<typeof setInterval>;
}

@Injectable({
  providedIn: 'root',
})
export class QueueNotificationService {
  private readonly signalRService = inject(SignalRService);
  private readonly http = inject(HttpService);
  private readonly fileService = inject(FileService);
  private readonly notificationService = inject(NotificationService);
  private readonly myDriveService = inject(MyDriveService);
  private readonly dialog = inject(MatDialog);
  private readonly userService = inject(UserService);
  private readonly translate = inject(TranslateService);

  private readonly pendingOperations = new Map<string, TrackedQueueOperation>();
  private readonly earlyNotifications = new Map<string, QueueNotificationModel>();
  private readonly settledOperations = new Set<string>();

  constructor() {
    this.signalRService.registerHandler(
      HubHandlerSourceAngular,
      QUEUE_NOTIFICATION_HUB_METHOD,
      (notification: QueueNotificationModel) => this.onQueueNotification(notification),
    );
  }

  /**
   * Registers a newly enqueued background operation, displays its "preparing" notification,
   * and starts fallback status recovery polling until the operation settles or times out.
   */
  registerOperation<TPayload extends QueuePendingPayload>(operation: QueuePendingOperation<TPayload>): void {
    if (!operation?.operationId) {
      return;
    }

    this.clearTrackedOperation(operation.operationId);
    this.settledOperations.delete(operation.operationId);

    const tracked: TrackedQueueOperation = {
      operation,
      startedAt: Date.now(),
    };
    this.pendingOperations.set(operation.operationId, tracked);

    const earlyNotification = this.earlyNotifications.get(operation.operationId);
    if (earlyNotification) {
      this.earlyNotifications.delete(operation.operationId);
      this.onQueueNotification(earlyNotification);
      return;
    }

    this.startStatusRecoveryPolling(tracked);
  }

  /**
   * Cancels client-side tracking for an in-flight operation (e.g., if the user closes or cancels).
   */
  cancelOperation(operationId: string): void {
    if (!operationId) {
      return;
    }
    this.clearTrackedOperation(operationId);
    this.earlyNotifications.delete(operationId);
    this.markOperationSettled(operationId);
  }

  /**
   * Displays a notification informing the user that the operation continues in the background
   * after they closed the waiting dialog.
   */
  notifyContinueInBackground(jobType: QueueJobType): void {
    this.notificationService.success(this.getContinueInBackgroundTranslationKey(jobType));
  }

  private onQueueNotification(notification: QueueNotificationModel<QueueNotificationPayload>): void {
    if (!notification?.operationId || this.settledOperations.has(notification.operationId)) {
      return;
    }

    const tracked = this.pendingOperations.get(notification.operationId);
    const pending = tracked?.operation;
    if (!pending) {
      if (notification.status === QueueJobStatus.Completed || notification.status === QueueJobStatus.Failed) {
        this.earlyNotifications.set(notification.operationId, notification);
      }
      return;
    }

    const jobType = pending.jobType ?? notification.jobType ?? QueueJobType.Print;

    if (notification.status === QueueJobStatus.Completed) {
      this.markOperationSettled(notification.operationId);
      this.clearTrackedOperation(notification.operationId);
      this.earlyNotifications.delete(notification.operationId);
      this.handleCompleted(notification, pending, jobType);
    } else if (notification.status === QueueJobStatus.Failed) {
      this.markOperationSettled(notification.operationId);
      this.clearTrackedOperation(notification.operationId);
      this.earlyNotifications.delete(notification.operationId);
      this.notificationService.error(this.formatFailedErrorMessage(jobType, notification.errorMessage));
      pending.onSettled?.(QueueJobStatus.Failed);
    }
  }

  private startStatusRecoveryPolling(tracked: TrackedQueueOperation): void {
    const { operation } = tracked;
    const maxWaitMs = this.getMaxWaitMs(operation.jobType);
    tracked.pollIntervalId = setInterval(() => {
      if (!this.pendingOperations.has(operation.operationId)) {
        this.clearTrackedOperation(operation.operationId);
        return;
      }

      if (Date.now() - tracked.startedAt >= maxWaitMs) {
        this.onQueueNotification({
          operationId: operation.operationId,
          jobType: operation.jobType,
          status: QueueJobStatus.Failed,
        });
        return;
      }

      const params = this.http.buildHttpParams({
        jobId: operation.jobId,
        jobType: operation.jobType,
      });

      this.http
        .get<QueueNotificationModel<QueueFileNotificationPayload>>(
          `_api/queue/operations/${encodeURIComponent(operation.operationId)}/status`,
          {
            httpOptions: { params },
            useLoader: false,
            useErrorNotification: false,
          },
        )
        .subscribe({
          next: statusModel => {
            if (
              statusModel &&
              (statusModel.status === QueueJobStatus.Completed || statusModel.status === QueueJobStatus.Failed)
            ) {
              this.onQueueNotification(statusModel);
            }
          },
          error: () => {
            // Ignore transient polling errors; next poll or SignalR event will recover.
          },
        });
    }, QUEUE_STATUS_POLL_INTERVAL_MS);
  }

  private clearTrackedOperation(operationId: string): void {
    const existing = this.pendingOperations.get(operationId);
    if (existing?.pollIntervalId) {
      clearInterval(existing.pollIntervalId);
    }
    this.pendingOperations.delete(operationId);
  }

  private markOperationSettled(operationId: string): void {
    this.settledOperations.add(operationId);
    setTimeout(() => this.settledOperations.delete(operationId), SETTLED_OPERATION_TTL_MS);
  }

  private handleCompleted(
    notification: QueueNotificationModel<QueueNotificationPayload>,
    pending: QueuePendingOperation | undefined,
    jobType: QueueJobType,
  ): void {
    switch (jobType) {
      case QueueJobType.Print:
      case QueueJobType.Download:
      case QueueJobType.Email:
        this.handleFileOperationCompleted(
          notification as QueueNotificationModel<QueueFileNotificationPayload>,
          pending as QueuePendingOperation<QueueFilePendingPayload> | undefined,
          jobType,
        );
        break;
    }
  }

  private handleFileOperationCompleted(
    notification: QueueNotificationModel<QueueFileNotificationPayload>,
    pending: QueuePendingOperation<QueueFilePendingPayload> | undefined,
    jobType: QueueJobType,
  ): void {
    const fileName = notification.payload?.fileName || pending?.payload?.fileName || 'Document.pdf';
    const contentType = notification.payload?.contentType || 'application/pdf';

    this.http
      .getBlob(`_api/queue/files/${encodeURIComponent(notification.operationId)}`, {
        useLoader: false,
        useErrorNotification: false,
      })
      .subscribe({
        next: blob => {
          const typedBlob = blob.type ? blob : new Blob([blob], { type: contentType });

          if (pending?.onFileReady) {
            pending.onFileReady(typedBlob, fileName, contentType);
            pending.onSettled?.(QueueJobStatus.Completed);
            return;
          }

          switch (jobType) {
            case QueueJobType.Print:
              printBlob(typedBlob);
              pending?.onSettled?.(QueueJobStatus.Completed);
              break;
            case QueueJobType.Download:
              this.fileService.downloadFile(typedBlob, fileName);
              pending?.onSettled?.(QueueJobStatus.Completed);
              break;
            case QueueJobType.Email:
              if (this.isEmailPendingPayload(pending?.payload)) {
                this.handleEmailFile(typedBlob, fileName, contentType, pending.payload, pending?.onSettled);
              } else {
                this.fileService.downloadFile(typedBlob, fileName);
                pending?.onSettled?.(QueueJobStatus.Completed);
              }
              break;
          }
        },
        error: () => {
          this.notificationService.error(this.getFailedTranslationKey(jobType));
          pending?.onSettled?.(QueueJobStatus.Failed);
        },
      });
  }

  private isEmailPendingPayload(payload: QueuePendingPayload | undefined): payload is QueueEmailPendingPayload {
    return !!payload && 'moduleName' in payload && 'entityInstanceIds' in payload;
  }

  private handleEmailFile(
    blob: Blob,
    fileName: string,
    contentType: string,
    emailPayload: QueueEmailPendingPayload,
    onSettled?: (status: QueueJobStatus.Completed | QueueJobStatus.Failed) => void,
  ): void {
    const file = new File([blob], fileName, { type: contentType });
    const folderData: GetFolderParams = {
      id: 0,
      name: `${emailPayload.moduleName} - Shared`,
      parentId: -1,
    };

    this.myDriveService
      .getFolderId(folderData)
      .pipe(switchMap(folderId => this.myDriveService.uploadFiles([file], folderId, contentType)))
      .subscribe({
        next: (uploadedAttachments: AttachmentModel[]) => {
          const attachmentsInMB = uploadedAttachments.map(a => ({
            ...a,
            length: (a.length || 0) / 1024 / 1024,
          }));
          onSettled?.(QueueJobStatus.Completed);
          this.openInboxDialog(attachmentsInMB, emailPayload);
        },
        error: () => {
          this.notificationService.error(this.getFailedTranslationKey(QueueJobType.Email));
          onSettled?.(QueueJobStatus.Failed);
        },
      });
  }

  private openInboxDialog(attachments: AttachmentModel[], emailPayload: QueueEmailPendingPayload): void {
    import('@module-instance/inbox/inbox-dialog/inbox-dialog.component').then(m => {
      this.dialog.open(m.InboxDialogComponent, {
        data: {
          contractId: emailPayload.contractId,
          contractNo: emailPayload.contractNo,
          moduleId: emailPayload.moduleId,
          moduleName: emailPayload.moduleName,
          moduleCode: emailPayload.moduleCode,
          attachments,
          entityInstanceIds: emailPayload.entityInstanceIds,
        },
        panelClass: ['cipo', 'cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().lg,
        disableClose: false,
      });
    });
  }

  private getMaxWaitMs(jobType: QueueJobType): number {
    switch (jobType) {
      case QueueJobType.Print:
        return QUEUE_PRINT_OPERATION_MAX_WAIT_MS;
      default:
        return QUEUE_OPERATION_MAX_WAIT_MS;
    }
  }

  private getContinueInBackgroundTranslationKey(jobType: QueueJobType): string {
    switch (jobType) {
      case QueueJobType.Download:
        return 'queue.download.continueInBackground';
      case QueueJobType.Email:
        return 'queue.email.continueInBackground';
      case QueueJobType.Print:
      default:
        return 'queue.print.continueInBackground';
    }
  }

  private getFailedTranslationKey(jobType: QueueJobType): string {
    switch (jobType) {
      case QueueJobType.Download:
        return 'queue.download.failed';
      case QueueJobType.Email:
        return 'queue.email.failed';
      case QueueJobType.Print:
      default:
        return 'queue.print.failed';
    }
  }

  private formatFailedErrorMessage(jobType: QueueJobType, errorMessage?: string): string {
    const translationKey = this.getFailedTranslationKey(jobType);
    const trimmedError = errorMessage?.trim();
    if (!trimmedError) {
      return translationKey;
    }

    const translatedMessage = this.translate.instant(translationKey);
    return `${translatedMessage} ${trimmedError}`;
  }
}
